import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "relative z-10 max-w-7xl" }

import { nextTick, onActivated, onMounted, ref } from "vue";
import { Motion, motion, useScroll, useTransform } from "motion-v";


export default /*@__PURE__*/_defineComponent({
  __name: 'VideoShowcaseCard',
  setup(__props) {

const containerRef = ref<HTMLElement | null>(null);

// Track scroll progress of the container
const { scrollYProgress } = useScroll({
  target: containerRef,
  offset: ["start end", "end center"],
});

// Scroll zoom is transform-only (scale), so it never triggers layout/reflow.
// Animating width/padding here caused CLS + non-composited paint on every frame.
const scale = useTransform(scrollYProgress, [0, 0.5], [0.6, 1]);
// Smooth border radius animation: 16px → 0px based on scroll progress
const borderRadius = useTransform(scrollYProgress, [0, 1], ["16px", "0px"]);

// Smooth parallax effect on video
const videoY = useTransform(scrollYProgress, [0, 1], [0, -100]);

const videoRef = ref<{ $el: HTMLVideoElement } | null>(null);

const ensureVideoPlays = async () => {
  await nextTick();

  const video = videoRef.value?.$el;
  if (!video) return;

  try {
    video.muted = true;
    video.playsInline = true;

    if (video.paused) {
      const p = video.play();
      if (p && typeof (p as Promise<void>).catch === "function") {
        (p as Promise<void>).catch(() => {});
      }
    }
  } catch {
    // No-op
  }
};

onMounted(() => {
  // Only start the video once the user scrolls it into view. Combined with
  // preload="none" this means the 3.5MB mp4 is never downloaded unless the
  // visitor actually reaches the showcase section.
  const video = videoRef.value?.$el;
  if (!video) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          ensureVideoPlays();
          observer.disconnect();
        }
      }
    },
    { threshold: 0.3 },
  );
  observer.observe(video);
});

onActivated(() => {
  ensureVideoPlays();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Motion), {
    ref_key: "containerRef",
    ref: containerRef,
    class: "mx-auto mt-12 sm:mt-16"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(Motion), {
        style: _normalizeStyle({ scale: _unref(scale), borderRadius: _unref(borderRadius) }),
        class: "p-6 md:p-12 lg:p-20 mx-auto flex justify-center items-center px-auto styled-card relative overflow-hidden select-text touch-none z-30 bg-gradient-to-br from-purple-100 via-indigo-50 to-pink-100 dark:from-slate-900 dark:via-purple-900/50 dark:to-indigo-900/50 rounded-lg sm:rounded-xl lg:rounded-2xl will-change-transform"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Motion), {
            style: _normalizeStyle({ borderRadius: _unref(borderRadius) }),
            class: "absolute inset-0 pointer-events-none overflow-hidden rounded-lg sm:rounded-xl lg:rounded-2xl"
          }, {
            default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
              _createElementVNode("div", { class: "absolute top-10 left-10 w-20 h-20 sm:w-32 sm:h-32 bg-gradient-to-r from-purple-400/30 to-pink-400/30 rounded-full blur-xl animate-pulse" }, null, -1),
              _createElementVNode("div", { class: "absolute top-20 right-10 sm:right-20 w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-r from-indigo-400/20 to-purple-400/20 rounded-full blur-lg animate-bounce slow" }, null, -1),
              _createElementVNode("div", { class: "absolute bottom-10 left-1/4 sm:left-1/3 w-24 h-24 sm:w-40 sm:h-40 bg-gradient-to-r from-pink-300/20 to-purple-300/20 rounded-full blur-2xl animate-pulse delay-1000" }, null, -1),
              _createElementVNode("div", { class: "absolute bottom-20 right-10 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-indigo-300/25 to-blue-300/25 rounded-full blur-md animate-bounce delay-500" }, null, -1),
              _createElementVNode("div", { class: "absolute -bottom-20 -left-20 w-40 h-40 sm:w-60 sm:h-60 bg-gradient-to-r from-indigo-300/25 to-purple-400/25 rounded-full blur-xl animate-pulse delay-2000" }, null, -1),
              _createElementVNode("div", { class: "absolute top-2/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-300/30 to-transparent animate-pulse" }, null, -1),
              _createElementVNode("div", { class: "absolute bottom-1/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-indigo-300/20 to-transparent animate-pulse delay-700" }, null, -1)
            ]))]),
            _: 1
          }, 8, ["style"]),
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_unref(motion).video, {
              ref_key: "videoRef",
              ref: videoRef,
              key: "task",
              animate: { y: _unref(videoY) },
              transition: {
            y: { type: false }, // Instant update for smooth scroll-linked animation
          },
              poster: "/demo.webp",
              src: "/demo.mp4",
              loop: "",
              muted: "",
              playsinline: "",
              "disable-picture-in-picture": "",
              "disable-remote-playback": "",
              preload: "none",
              "aria-hidden": "true",
              class: "w-full aspect-video rounded-sm sm:rounded-lg md:rounded-xl shadow-2xl pointer-events-none select-none object-cover dark:block"
            }, null, 8, ["animate"])
          ])
        ]),
        _: 1
      }, 8, ["style"])
    ]),
    _: 1
  }, 512))
}
}

})